import { useQuery } from "@tanstack/react-query";
import React, { memo, useState } from "react";
import { withNamespaces } from "react-i18next";
import { toast } from "react-toastify";
import { Alert, Card, CardBody, CardTitle, Col, Row, Spinner, Table } from "reactstrap";
import LicenseManagementService from "src/modules/3rd-party-management/apis/admin/LicenseManagementService";
import ReactApexChart from "react-apexcharts";
import DateUtils from "src/services/utils/DateUtils";
import {
    EMPTY_LIST,
    INTERNATIONAL_DATE_FORMAT
} from 'src/common/constants';
import { isFeatureEnabledForActiveModule } from "src/helpers/module_helper";
import BootstrapTable from "react-bootstrap-table-next";
import overlayFactory from 'react-bootstrap-table2-overlay';
import paginationFactory, { 
    PaginationProvider, 
    PaginationListStandalone,
    SizePerPageDropdownStandalone,
    PaginationTotalStandalone
} from 'react-bootstrap-table2-paginator';

const LoadingContent = () => {
    return (
        <div className="overview-tab">
            <Row className="mb-3">
                <Col lg="4">
                    <div className="dt-field dt-skeleton" style={{ width: '100%', height: '120px' }}></div>
                </Col>
                <Col lg="4">
                    <div className="dt-field dt-skeleton" style={{ width: '100%', height: '120px' }}></div>
                </Col>
                <Col lg="4">
                    <div className="dt-field dt-skeleton" style={{ width: '100%', height: '120px' }}></div>
                </Col>
            </Row>
            <Row>
                <Col lg="12">
                    <div className="dt-field dt-skeleton" style={{ width: '100%', height: '240px' }}></div>
                </Col>
            </Row>
        </div>
    )
}

const CreditHistory = memo(({ t, fetchOverviewQuery }) => {
    const [filters, setFilters] = useState({
        pageIndex: 1,
        pageSize: 50,
        sortField: null,
        sortOrder: null
    });

    const dateUtils = new DateUtils(); 

    const logs = fetchOverviewQuery.data?.logs || [];

    const tableColumns = [
        {
            dataField: "id",
            text: t("ID"),
            sort: true,
            style: {
                width: '80px'
            }
        },
        {
            dataField: "action",
            text: t("Action"),
            sort: true,
            style: {
                width: '120px'
            },
            formatter: (cellContent) => {
                switch (cellContent) {
                    case 'register_plot':
                        return t('Plot Registration');
                    default:
                        return cellContent;
                }
            }
        },
        {
            dataField: "area",
            text: `${t("Used {{creditsLabel}}", {
                creditsLabel: 'Credits'
            })}`,
            sort: true,
            style: {
                width: '120px'
            },
            formatter: (cellContent) => {
                return parseFloat(cellContent).toLocaleString();
            }
        },
        {
            dataField: "createdAt",
            text: t("Date"),
            sort: true,
            style: {
                width: '120px'
            },
            formatter: (cellContent) => {
                return dateUtils.convertDateToDate(cellContent, INTERNATIONAL_DATE_FORMAT)
            }
        },
    ];

    const NoDataIndication = () => {
        if (fetchOverviewQuery.isFetched && !logs.length) {
            return (
                <div className="alert m-0" role="alert">
                    <p style={{
                        textAlign: "center",
                        marginBottom: 0
                    }}>
                        {t(EMPTY_LIST)}
                    </p>
                </div>
            )
        }

        return <></>
    }

    const handleTableChange = (type, options) => {
        const {
            page,
            sizePerPage,

            sortField,
            sortOrder
        } = options;

        switch (type) {
            case 'pagination':
                setFilters({
                    ...filters,
                    pageIndex: page || 1,
                    pageSize: sizePerPage
                });
                break;
            case 'sort':
                setFilters({
                    ...filters,
                    sortField: sortField,
                    sortOrder: sortOrder.toUpperCase()
                })
                break;
            default:
                return false;
        }
    }

    return (
        <Row>
            <Col sm='12'>
                <Card>
                    <CardBody>
                        <CardTitle tag="h4">
                            {t('History Logs')}
                        </CardTitle>

                        <PaginationProvider
                            pagination={
                                paginationFactory({
                                    custom: true,
                                    page: filters.pageIndex,
                                    sizePerPage: filters.pageSize,
                                    totalSize: logs.length,
                                    withFirstAndLast: true,
                                    alwaysShowAllBtns: true,
                                    prePageText: <span><i className="ri-arrow-left-s-line"></i> {t('Back')}</span>,
                                    nextPageText: <span>{t('Next')} <i className="ri-arrow-right-s-line"></i></span>,
                                    prePageTitle: t('Pre page'),
                                    firstPageTitle: t('First page'),
                                    lastPageTitle: t('Last page'),
                                    showTotal: false,
                                    paginationTotalRenderer: (from, to, size) => {
                                        return (
                                            <span className="react-bootstrap-table-pagination-total">
                                                {`${t('Total Items')} ${size}`}
                                            </span>
                                        )
                                    },
                                    hideSizePerPage: false,
                                    sizePerPageList: [
                                        {
                                            text: '25',
                                            value: 25
                                        },
                                        {
                                            text: '50',
                                            value: 50
                                        }
                                    ]
                                })
                            }>
                            {({
                                paginationProps,
                                paginationTableProps
                            }) => (
                                <>
                                    <Row>
                                        <Col sm="12">
                                            <BootstrapTable
                                                loading={fetchOverviewQuery.isFetching}
                                                remote={{
                                                    pagination: true,
                                                    filter: false,
                                                    sort: true,
                                                    cellEdit: false,
                                                    search: false
                                                }}
                                                overlay={
                                                    overlayFactory({
                                                        spinner: <Spinner animation="border" variant="primary" size="md" />,
                                                        text: `${t("Loading")}...`
                                                    })
                                                }
                                                keyField={"id"}
                                                responsive
                                                bordered={false}
                                                data={logs}
                                                striped={true}
                                                columns={tableColumns}
                                                onTableChange={handleTableChange}
                                                wrapperClasses="table-responsive"
                                                classes={"table tpdd-table"}
                                                headerWrapperClasses={"thead-light"}
                                                style={{
                                                    overflowX: "auto",
                                                }}
                                                noDataIndication={() => <NoDataIndication />}
                                                {...paginationTableProps}
                                            />
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col sm="12">
                                            <div className="tpdd-pagination-style-1">
                                                <PaginationTotalStandalone
                                                    {...paginationProps}
                                                />

                                                <div>
                                                    <PaginationListStandalone
                                                        {...paginationProps}
                                                    />

                                                    <SizePerPageDropdownStandalone
                                                        {...paginationProps}
                                                    />
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </>
                            )}
                        </PaginationProvider>
                    </CardBody>
                </Card>
            </Col>
        </Row>
    )
})

const GeoCledian = ({ t }) => {
    const dateUtils = new DateUtils();

    const fetchOverviewQuery = useQuery({
        queryKey: [
            '3rd-party-management-fetch-organization-license-overview',
            'geocledian'
        ],
        queryFn: async () => {
            const service = LicenseManagementService.getInstance();

            return await service.fetchEUDRLicenseDetail({});
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        enabled: isFeatureEnabledForActiveModule('eudr_activation'),
        onError: () => {
            toast(t('An error occurred while fetching overview content.'), {
                type: 'error'
            });
        },
    });

    if (!isFeatureEnabledForActiveModule('eudr_activation')) {
        return (
            <Alert
                color="warning"
            >
                <p className="mb-0">
                    {t("Please upgrade your plan to access this feature")}.
                </p>
            </Alert>
        )
    }

    if (fetchOverviewQuery.isFetching) return <LoadingContent />;

    const { totalArea, totalCredits, creditTransactions } = fetchOverviewQuery?.data || {};

    const remaining = totalCredits - totalArea;
    const usedPercent = totalCredits > 0 ? ((totalArea / totalCredits) * 100) : 0;
    const remainingPercent = totalCredits > 0 ? ((remaining / totalCredits) * 100) : 0;

    return (
        <div className="overview-tab">
            <Row>
                <Col sm="12" md="4">
                    <Card className="report-statistics-card">
                        <CardBody>
                            <Row>
                                <Col sm="6" md="8">
                                    <div className="overflow-hidden">
                                        <p className="text-truncate">
                                            {t('Total {{creditsLabel}}', {
                                                creditsLabel: 'Credits'
                                            })}
                                        </p>
                                        <h4 className="mb-0">
                                            {totalCredits}
                                        </h4>
                                    </div>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>

                <Col sm="12" md="4">
                    <Card className="report-statistics-card">
                        <CardBody>
                            <Row>
                                <Col sm="6" md="8">
                                    <div className="overflow-hidden">
                                        <p className="text-truncate">
                                            {t('Used {{creditsLabel}}', {
                                                creditsLabel: 'Credits'
                                            })}
                                        </p>
                                        <h4 className="mb-0">
                                            {totalArea}
                                        </h4>
                                    </div>
                                </Col>

                                <Col sm="6" md="4">
                                    <ReactApexChart
                                        options={{
                                            chart: {
                                                sparkline: {
                                                    enabled: true
                                                }
                                            },
                                            dataLabels: {
                                                enabled: true
                                            },
                                            colors: ['#FF4949'],
                                            stroke: {
                                                lineCap: 'round'
                                            },
                                            plotOptions: {
                                                radialBar: {
                                                    hollow: {
                                                        margin: 0,
                                                        size: '70%'
                                                    },
                                                    track: {
                                                        margin: 0,
                                                    },
                                                    dataLabels: {
                                                        show: true,
                                                        name: {
                                                            show: false
                                                        },
                                                        value: {
                                                            show: true,
                                                            offsetY: 6
                                                        }
                                                    }
                                                }
                                            }
                                        }}
                                        series={[usedPercent.toFixed(1)]}
                                        type="radialBar"
                                        height="60"
                                    />
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>

                <Col sm="12" md="4">
                    <Card className="report-statistics-card">
                        <CardBody>
                            <Row>
                                <Col sm="6" md="8">
                                    <div className="overflow-hidden">
                                        <p className="text-truncate">
                                            {t('Remaining {{creditsLabel}}', {
                                                creditsLabel: 'Credits'
                                            })}
                                        </p>
                                        <h4 className="mb-0">
                                            {remaining.toLocaleString()}
                                        </h4>
                                    </div>
                                </Col>

                                <Col sm="6" md="4">
                                    <ReactApexChart
                                        options={{
                                            chart: {
                                                sparkline: {
                                                    enabled: true
                                                }
                                            },
                                            dataLabels: {
                                                enabled: true
                                            },
                                            colors: ['#FF4949'],
                                            stroke: {
                                                lineCap: 'round'
                                            },
                                            plotOptions: {
                                                radialBar: {
                                                    hollow: {
                                                        margin: 0,
                                                        size: '70%'
                                                    },
                                                    track: {
                                                        margin: 0,
                                                    },
                                                    dataLabels: {
                                                        show: true,
                                                        name: {
                                                            show: false
                                                        },
                                                        value: {
                                                            show: true,
                                                            offsetY: 6
                                                        }
                                                    }
                                                }
                                            }
                                        }}
                                        series={[remainingPercent.toFixed(1)]}
                                        type="radialBar"
                                        height="60"
                                    />
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
                
                <Col sm="12" md="12">
                    <Card>
                        <CardBody>
                            <CardTitle tag="h4">
                                {t('{{creditLabel}} Transactions', {
                                    creditLabel: 'Credit'
                                })}
                            </CardTitle>
                            <Table responsive bordered>
                                <thead>
                                    <tr>
                                        <th>{t('Transaction ID')}</th>
                                        <th>Credit</th>
                                        <th>{t('Note')}</th>
                                        <th>{t('Date')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {creditTransactions.length > 0 ? (
                                        creditTransactions.map((tx) => (
                                            <tr key={tx.id}>
                                                <td>
                                                    {tx.id}
                                                </td>
                                                <td>
                                                    {tx.licenseCount.toLocaleString()}
                                                </td>
                                                <td title={tx.note}>
                                                    {tx.note}
                                                </td>
                                                <td>
                                                    {dateUtils.convertDateToDate(tx.createdAt, INTERNATIONAL_DATE_FORMAT)}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="4" className="text-center">
                                                {t(EMPTY_LIST)}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </CardBody>
                    </Card>
                </Col>

                <Col sm="12" md="12">
                    <CreditHistory 
                        fetchOverviewQuery={fetchOverviewQuery}
                        t={t}
                    />
                </Col>
            </Row>
        </div>
    );
};

export default withNamespaces()(
    GeoCledian
);
